import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  ArrowPathIcon,
  MapPinIcon,
  EnvelopeIcon,
  PrinterIcon,
  SparklesIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';

const RefundPolicy: React.FC = () => {
  // Ensure page starts at top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  const effectiveDate = 'May 15, 2026';

  return (
    <div className="min-h-screen py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold text-red-400 bg-red-400/10 border border-red-400/20 rounded-full mb-4">
            <CurrencyDollarIcon className="w-4 h-4" />
            Refund Policy
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-text-main mb-4 tracking-tight leading-tight">
            Refund Policy
          </h1>
          <p className="text-lg text-text-muted max-w-2xl mx-auto">
            Effective Date: <span className="text-text-main font-medium">{effectiveDate}</span>  ·  Applies to all NotesCrafter platforms
          </p>
        </motion.div>

        {/* Section 1: Our General Policy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-glass/30 backdrop-blur-md border border-border-glow rounded-2xl p-8 mb-8 hover:border-primary/50 transition-colors shadow-xl"
        >
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/30 p-4 rounded-xl shadow-inner">
              <ShieldExclamationIcon className="w-8 h-8 text-red-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-text-main mb-3">1. Our General Policy</h2>
              <p className="text-text-muted leading-relaxed">
                NotesCrafter operates two paid services: <span className="text-text-main font-semibold">Community Pass</span> (digital membership) and <span className="text-text-main font-semibold">Print Orders</span> (physical product). Due to the nature of both services, all purchases are final and non-refundable, except in the limited circumstances described in Section 5.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Detailed Sections */}
        <div className="space-y-8">
          
          {/* Section 2: Community Pass — No Refunds */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <SparklesIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">2. Community Pass — No Refunds</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-4">
              <p className="text-text-muted leading-relaxed">
                All Community Pass purchases — <span className="text-text-main font-medium">Silver, Gold, Platinum, and Diamond</span> — are strictly non-refundable.
              </p>
              <div className="bg-red-500/5 border border-red-500/10 rounded-xl p-5 my-2">
                <p className="text-sm text-red-300 font-semibold mb-2">By completing a Community Pass purchase, you acknowledge and agree that:</p>
                <ul className="space-y-2.5 text-text-muted text-sm leading-relaxed">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                    <span>You are purchasing immediate access to digital premium features</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                    <span>The service is made available to you instantly upon payment confirmation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                    <span>No refund will be issued regardless of usage, remaining access duration, or personal circumstances</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                    <span>Passes are non-transferable and cannot be exchanged for cash or credit</span>
                  </li>
                </ul>
              </div>
              <p className="text-text-muted leading-relaxed">
                If you are uncertain about which tier suits you, we encourage you to use the free tier before purchasing.
              </p>
            </div>
          </motion.div>

          {/* Section 3: Community Pass Upgrades — No Refunds */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <ArrowPathIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">3. Community Pass Upgrades — No Refunds</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-4 text-text-muted leading-relaxed">
              <p>
                Upgrade payments — the net amount charged when moving from a lower-tier to a higher-tier pass — are also strictly non-refundable. The prorated remaining value of your existing pass is applied as a discount at the time of upgrade and cannot be reclaimed as a refund after the upgrade is processed.
              </p>
              <p>
                Once an upgrade is completed, the original pass is superseded and no value from either the original or the upgraded pass can be refunded.
              </p>
            </div>
          </motion.div>

          {/* Section 4: Print Orders — No Refunds */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <PrinterIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">4. Print Orders — No Refunds</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-4 text-text-muted leading-relaxed">
              <p>
                All print orders placed on <code className="px-1.5 py-0.5 text-xs text-primary bg-primary/10 border border-primary/20 rounded">print.notescrafter.com</code> or via the Android app are custom-manufactured specifically for you and are therefore strictly non-refundable, including:
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm font-medium">
                {[
                  'Orders confirmed and accepted',
                  'Orders currently in production',
                  'Orders in transit or out for delivery',
                  'Delivered orders'
                ].map((item, i) => (
                  <li key={i} className="bg-red-500/5 border border-red-500/10 p-3 rounded-lg text-red-300 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="bg-primary/5 border border-primary/10 rounded-xl p-4 text-sm mt-3">
                Because each notebook is printed and bound to your file's specifications, we are unable to resell or reuse returned items. Please carefully review your PDF — <strong className="text-text-main">page count, orientation, content, and image quality</strong> — before placing an order.
              </p>
              <p className="text-sm font-semibold text-text-main">
                By completing payment, you confirm you have reviewed your file and accept these terms.
              </p>
            </div>
          </motion.div>

          {/* Section 5: Exceptions — When We May Assist */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <ExclamationTriangleIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">5. Exceptions — When We May Assist</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-4 text-text-muted leading-relaxed">
              <p>
                Exceptions are considered only in the following specific situations:
              </p>
              <ul className="space-y-3 mb-4 bg-primary/5 p-4 rounded-lg border border-primary/10">
                <li className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <span>Your order was never shipped and no tracking information was generated within <strong className="text-text-main">5 business days</strong> of order confirmation.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <span>You received a product with a clear manufacturing defect attributable to our production (e.g., blank pages, severe misprint, missing sections) and <strong className="text-text-main">not caused</strong> by the quality or formatting of your uploaded PDF.</span>
                </li>
              </ul>
              
              <div className="border-t border-border-glow pt-4 mt-2">
                <h4 className="font-bold text-text-main mb-3">To raise an exception:</h4>
                <ol className="space-y-3 list-decimal list-inside text-sm">
                  <li>
                    <span>Contact <a href="mailto:support@notescrafter.com" className="text-primary hover:underline font-medium">support@notescrafter.com</a> within <strong className="text-text-main">48 hours of delivery</strong> (or within 48 hours of the expected delivery date if undelivered)</span>
                  </li>
                  <li>
                    <span>Include your <strong className="text-text-main">Order ID</strong>, a clear description of the issue, and supporting photographs</span>
                  </li>
                </ol>
              </div>
              <p className="text-sm bg-red-500/5 border border-red-500/10 p-4 rounded-lg text-red-200 mt-4 leading-relaxed">
                We will review your request and, at our sole discretion, may offer a reprint or partial store credit. <strong className="text-red-300">Cash refunds are not provided even in exception cases.</strong>
              </p>
            </div>
          </motion.div>

          {/* Section 6: Payment Disputes & Chargebacks */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <ShieldExclamationIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">6. Payment Disputes & Chargebacks</h2>
            </div>
            <div className="pl-0 sm:pl-12 text-text-muted leading-relaxed space-y-3">
              <p>
                If you have a concern regarding a charge, please contact <a href="mailto:support@notescrafter.com" className="text-primary hover:underline font-medium">support@notescrafter.com</a> before initiating any dispute or chargeback with your bank or card issuer.
              </p>
              <p className="bg-red-500/5 border border-red-500/10 p-4 rounded-lg text-red-200 text-sm">
                Initiating a chargeback without first contacting us may result in immediate suspension of your NotesCrafter account and permanent revocation of any active Community Pass, without any obligation on our part to restore access or issue a refund.
              </p>
            </div>
          </motion.div>

          {/* Section 7: Razorpay Transactions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <CreditCardIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">7. Razorpay Transactions</h2>
            </div>
            <div className="pl-0 sm:pl-12 text-text-muted leading-relaxed">
              <p>
                All payments are processed by Razorpay. In the event of a payment failure or duplicate charge at the gateway level, please contact us at <a href="mailto:support@notescrafter.com" className="text-primary hover:underline font-medium">support@notescrafter.com</a> and we will coordinate with Razorpay to resolve the issue promptly.
              </p>
            </div>
          </motion.div>

          {/* Section 8: Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-glass/30 backdrop-blur-md border border-primary/20 rounded-xl p-8 hover:border-primary/40 transition-colors shadow-xl"
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div>
                <h2 className="text-2xl font-bold text-text-main mb-2">8. Contact Us</h2>
                <div className="space-y-1 text-text-muted text-sm sm:text-base">
                  <p className="font-semibold text-text-main">NotesCrafter</p>
                  <p className="flex items-center gap-1.5">
                    <MapPinIcon className="w-4 h-4 text-primary flex-shrink-0" />
                    Sole Proprietorship · Aurangabad, Bihar, India
                  </p>
                </div>
              </div>
              <a
                href="mailto:support@notescrafter.com"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary-hover text-white font-semibold rounded-xl transition-all shadow-lg shadow-primary/25 hover:scale-[1.02] active:scale-[0.98]"
              >
                <EnvelopeIcon className="w-5 h-5" />
                support@notescrafter.com
              </a>
            </div>
          </motion.div>

        </div>

        {/* Bottom Warning/Updates box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mt-12 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6"
        >
          <div className="flex items-start space-x-3">
            <ExclamationTriangleIcon className="w-6 h-6 text-yellow-300 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-yellow-200 mb-1">Policy Updates</h3>
              <p className="text-yellow-200/80 text-sm leading-relaxed">
                We may update this Refund Policy from time to time. We will notify you of any significant changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
              </p>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default RefundPolicy;